import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import StatsCard from '../components/UI/StatsCard';
import Button from '../components/UI/Button';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  PlusIcon,
  WalletIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import FinancialCharts from '../components/financial/FinancialCharts';
import FinancialTable from '../components/financial/FinancialTable';
import AddExpenseModal from '../components/financial/AddExpenseModal';
import AddIncomeModal from '../components/financial/AddIncomeModal';
import financialService, { FinancialStats } from '../api/services/financial.service';

const FinancialManagement: React.FC = () => {
  const navigate = useNavigate();
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');

  const [fromDate, setFromDate] = useState('');
  const [fromTime, setFromTime] = useState('00:00');
  const [toDate, setToDate] = useState('');
  const [toTime, setToTime] = useState('23:59');

  const queryClient = useQueryClient();

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['financial-stats'],
    queryFn: () => financialService.getStats(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const listParams = useMemo(() => {
    const p: any = { page, limit };
    if (typeFilter && typeFilter !== 'all') p.type = typeFilter;
    if (searchTerm) p.search = searchTerm;
    if (fromDate) p.fromDate = fromDate;
    if (fromTime) p.fromTime = fromTime;
    if (toDate) p.toDate = toDate;
    if (toTime) p.toTime = toTime;
    return p;
  }, [
    page,
    limit,
    typeFilter,
    searchTerm,
    fromDate,
    fromTime,
    toDate,
    toTime,
  ]);

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useQuery({
    queryKey: ['financial-transactions', listParams],
    queryFn: () => financialService.getAll(listParams),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const handleTransactionAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['financial-stats'] });
    queryClient.invalidateQueries({ queryKey: ['financial-transactions'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
    setPage(1);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setTypeFilter('all');
    setPage(1);
  };

  const handleClearDateFilter = () => {
    setFromDate('');
    setFromTime('00:00');
    setToDate('');
    setToTime('23:59');
    setPage(1);
  };

  const handleApplyDateFilter = () => {
    setPage(1);
  };

  const transactions = transactionsData?.transactions || [];
  const pagination = transactionsData?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  };

  const defaultStats: FinancialStats = {
    totalIncome: 0,
    totalProfit: 0,
    totalExpenses: 0,
    todayIncome: 0,
    todayProfit: 0,
    todayExpenses: 0,
    netAccountBalance: 0,
    todayNetAccountBalance: 0,
    monthlyData: [],
  };

  const currentStats = stats || defaultStats;

  const statsData = [
    {
      title: 'Total Income',
      value: `ETB ${(currentStats.totalIncome || 0).toLocaleString()}`,
      todayValue: `ETB ${(currentStats.todayIncome || 0).toLocaleString()}`,
      icon: <CurrencyDollarIcon className="w-5 h-5" />,
      color: 'success' as const,
      showToday: true,
    },
    {
      title: 'Total Profit Dividend',
      value: `ETB ${(currentStats.totalProfit || 0).toLocaleString()}`,
      todayValue: `ETB ${(currentStats.todayProfit || 0).toLocaleString()}`,
      icon: <ChartBarIcon className="w-5 h-5" />,
      color: 'secondary' as const,
      showToday: true,
    },
    {
      title: 'Total Expenses',
      value: `ETB ${(currentStats.totalExpenses || 0).toLocaleString()}`,
      todayValue: `ETB ${(currentStats.todayExpenses || 0).toLocaleString()}`,
      icon: <BanknotesIcon className="w-5 h-5" />,
      color: 'warning' as const,
      showToday: true,
    },
    {
      title: 'Net Account Balance',
      value: `ETB ${(currentStats.netAccountBalance || 0).toLocaleString()}`,
      todayValue: `ETB ${(currentStats.todayNetAccountBalance || 0).toLocaleString()}`,
      icon: <WalletIcon className="w-5 h-5" />,
      color: 'primary' as const,
      showToday: true,
    },
  ];

  const isLoading = statsLoading || (transactionsLoading && transactions.length === 0);
  const error = statsError || transactionsError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-secondary-500 border-t-transparent" />
          <p className="mt-2 text-sm text-gray-500">Loading financial data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500">
            {error instanceof Error ? error.message : 'Failed to load financial data'}
          </p>
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['financial-stats'] });
              queryClient.invalidateQueries({ queryKey: ['financial-transactions'] });
            }}
            className="mt-4 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Financial Management
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            View financial reports and analytics.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate('/reconciliation')}
          >
            <CalculatorIcon className="w-4 h-4 mr-1.5" />
            Daily Reconciliation
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsIncomeModalOpen(true)}
          >
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add Income
          </Button>
          <Button size="sm" onClick={() => setIsExpenseModalOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add Expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statsData.map((stat, i) => (
          <StatsCard key={i} {...stat} />
        ))}
      </div>

      <FinancialCharts data={currentStats.monthlyData || []} />

      <FinancialTable
        transactions={transactions}
        itemsPerPage={limit}
        pagination={pagination}
        onPageChange={handlePageChange}
        onTypeFilterChange={handleTypeFilterChange}
        typeFilter={typeFilter}
        onSearch={handleSearchSubmit}
        onSearchChange={setLocalSearch}
        onSearchKeyDown={handleSearchKeyDown}
        searchValue={localSearch}
        onClearFilters={handleClearFilters}
        loading={transactionsLoading}
        hasFilters={!!searchTerm || typeFilter !== 'all'}

        fromDate={fromDate}
        fromTime={fromTime}
        toDate={toDate}
        toTime={toTime}
        onFromDateChange={setFromDate}
        onFromTimeChange={setFromTime}
        onToDateChange={setToDate}
        onToTimeChange={setToTime}
        onApplyDateFilter={handleApplyDateFilter}
        onClearDateFilter={handleClearDateFilter}
      />

      <AddExpenseModal
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        onSuccess={handleTransactionAdded}
        netAccountBalance={currentStats.netAccountBalance || 0}
      />
      <AddIncomeModal
        isOpen={isIncomeModalOpen}
        onClose={() => setIsIncomeModalOpen(false)}
        onSuccess={handleTransactionAdded}
      />
    </div>
  );
};

export default FinancialManagement;